import { IVerticalBarChartDataPoint, VerticalBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useWidgetDrillDownNavigate } from 'components/Dashboard/WidgetDrillDownContext';
import { globalStackTokensGapSmall } from 'globalStyles';
import { mapFromAudience } from 'mappings/audienceMapping';
import { Audience } from 'models/audience/audience';
import { AudienceDTO } from 'models/dto/audience/audienceDTO';
import { Widget61DTO } from 'models/dto/widgets/widget61DTO';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataLibraryReadResponseAudiences } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { sortOnString } from 'utils/sorting';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetLibraryReadResponseAudiencesProps extends IWidgetRendererProps {}

const WidgetLibraryReadResponseAudiences: FunctionComponent<IWidgetLibraryReadResponseAudiencesProps> = (
  props: IWidgetLibraryReadResponseAudiencesProps,
) => {
  const { t } = useTranslation(['translation', 'widgets', 'publication', 'adminGroups']);
  const appContext = useContext(AppContext);
  const navigate = useWidgetDrillDownNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<Widget61DTO | undefined>(undefined);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const data = await apiGetWidgetDataLibraryReadResponseAudiences(accessToken, props.abortSignal);

      setData(data);
    } catch (err) {
      const appErr = AppError.fromGraphError(err);
      appContext.setError(appErr);
    } finally {
      setIsLoading(false);
    }
  };

  const onClick = (audience: AudienceDTO) => {
    if (!hasUserFeatureGenericManager(appContext)) return;
    const url = `/library/publication#report@${audience.audienceId}`;
    navigate(url);
  };

  const getData = (): IVerticalBarChartDataPoint[] => {
    if (
      !data ||
      !data.userReadConfirmations ||
      data.userReadConfirmations?.length === 0 ||
      !data.audiences ||
      data.audiences?.length === 0
    )
      return [];

    //filter audiences that have read confirmations
    const audiencesToShow = data.audiences.filter((a) => {
      return data.userReadConfirmations?.some((urc) => urc.audienceIds?.idList?.includes(a.audienceId.toString()));
    });
    audiencesToShow.sort((a, b) => sortOnString(a.trans[0]?.name, b.trans[0]?.name));

    //map to datapoints
    const points = audiencesToShow.map((audienceDto) => {
      const audience = mapFromAudience(audienceDto, appContext.globalDataCache) as Audience;
      audience.setOrgAudienceName(t);

      //get the read confirmations for this audience
      const confirmations = data.userReadConfirmations?.filter(
        (urc) => urc.audienceIds?.idList?.includes(audience.audienceId.toString()),
      );
      //count all vs read for the selected version
      const read = confirmations?.filter((c) => c.readConfirmed !== undefined);
      const percentage = ((read?.length ?? 0) / (confirmations?.length || 1)) * 100;

      const item: IVerticalBarChartDataPoint = {
        x: audience.name,
        y: percentage || 0.0001, //cannot be zero else the bar does not show
        yAxisCalloutData: `${Math.round(percentage)}%`,
        onClick: () => onClick(audienceDto),
      };

      return item;
    });

    return points;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const points = getData();
  const hasData = points.length > 0;

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {!hasData && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPText>{t('widgets:LibraryReadResponseAudiences.NoData')}</IPText>
        </IPStack>
      )}
      {hasData && (
        <IPStack.Item grow>
          <VerticalBarChart
            data={points}
            wrapXAxisLables={true}
            hideLegend={true}
            enableGradient={true}
            barWidth={'auto'}
            yMinValue={0}
            yMaxValue={100}
            yAxisTickFormat={(value: number) => `${Math.round(value)}%`}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default WidgetLibraryReadResponseAudiences;
